/**
 * AllStationsList.js
 * In drawer component it is used to display the list of all weather stations organized by communities.
 * This component displays a list of all weather stations organized by communities.
 * It provides an interactive accordion interface where users can:
 * - View stations grouped by their respective communities
 * - Expand/collapse community sections
 * - Navigate to individual station details
 * - See station status indicators
 *
 * The component handles three types of stations:
 * 1. Regular community stations
 * 2. ECCC Weather Stations
 * 3. Research & Development stations
 *
 *
 * @component
 * @param {Function} onStationSelect - Callback function triggered when a station is selected
 */

import React, { useState } from "react";
import {
  Typography,
  Box,
  Grid,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Link } from "react-router-dom";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { stationMetaData } from "../../utils/rawData";
import { createUrlFriendlyName, getStatusIcon } from "../../utils/rawFunction";
import { useMemo } from "react";
import { useTranslation } from "../../context/TranslationContext";

function AllStationsList({ onStationSelect }) {
  // State to track which accordion panels are expanded
  const [expandedPanels, setExpandedPanels] = useState([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { translate, currentLanguage } = useTranslation();

  /**
   * Handles the expansion/collapse of accordion panels
   * @param {string} panel - The panel identifier
   * @param {Event} event - The event object
   * @param {boolean} isExpanded - Whether the panel is being expanded
   */
  const handleChange = (panel) => (event, isExpanded) => {
    setExpandedPanels((prevExpanded) =>
      isExpanded
        ? [...prevExpanded, panel]
        : prevExpanded.filter((p) => p !== panel)
    );
  };

  /**
   * Organizes stations into communities and special categories
   * This memoized function:
   * 1. Separates research stations and ECCC stations
   * 2. Groups regular stations by community
   * 3. Sorts stations alphabetically within each group
   * 4. Adds special categories (Research first, then Government) at the end
   */
  const communities = useMemo(() => {
    // Helper function to check if a station should be in R&D category
    const isResearchStation = (station) => {
      return (
        !station.Community || // handles null
        station.Community.trim() === ""|| // handles empty string
        station.Community === "WIRL" // handles WIRL
      );
    };

    // Helper function to check if a station is an ECCC Weather Station
    const isECCCStation = (station) => {
      return station.type === "ECCC_Weather_Station";
    };

    // Helper function to check if a station is an NTI Weather Station
    const isNTIStation = (station) => {
      return station.type === "NTI_Weather_Station";
    };

    // First, separate research stations, ECCC stations, and NTI stations
    const researchStations = stationMetaData.filter(isResearchStation);
    const ecccStations = stationMetaData.filter(isECCCStation);
    const ntiStations = stationMetaData.filter(isNTIStation);

    // Remove research, ECCC, and NTI stations from main grouping
    const regularStations = stationMetaData.filter(
      (station) =>
        !isResearchStation(station) &&
        !isECCCStation(station) &&
        !isNTIStation(station)
    );

    // Group regular stations by community
    const grouped = regularStations.reduce((acc, station) => {
      const { Community } = station;
      if (!acc[Community]) {
        acc[Community] = [];
      }
      acc[Community].push(station);
      return acc;
    }, {});

    // Convert to array and sort
    const sortedCommunities = Object.entries(grouped)
      .map(([community, stations]) => ({
        name: community,
        stations: stations.sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    // Combine research stations and NTI stations for the Research category
    const researchAndNTIStations = [...researchStations, ...ntiStations];
    if (researchAndNTIStations.length > 0) {
      sortedCommunities.push({
        name: "Research",
        stations: researchAndNTIStations.sort((a, b) =>
          a.name.localeCompare(b.name)
        ),
      });
    }

    // Add ECCC stations as Government category at the end
    if (ecccStations.length > 0) {
      sortedCommunities.push({
        name: "Government",
        stations: ecccStations.sort((a, b) => a.name.localeCompare(b.name)),
      });
    }

    return sortedCommunities;
  }, []);

  return (
    <Box
      sx={{
        height: "100vh",
        overflow: "auto",
        "&::-webkit-scrollbar": {
          display: "none",
        },
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        p: isMobile ? 1 : 2,
      }}
    >
      {/* Map through each community to create accordion sections */}
      {communities.map((community, index) => (
        <React.Fragment key={index}>
          {/* Main accordion component for each community */}
          <Accordion
            key={index}
            expanded={expandedPanels.includes(`panel${index}`)}
            onChange={handleChange(`panel${index}`)}
            disableGutters
            elevation={0}
            sx={{
              "&:before": {
                display: "none",
              },
              "& .MuiAccordionSummary-root": {
                minHeight: isMobile ? 48 : 56,
                px: isMobile ? 1 : 2,
              },
            }}
          >
            {/* Accordion header with community name */}
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                flexDirection: "row-reverse",
                "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                  transform: "rotate(-90deg)",
                },
                "& .MuiAccordionSummary-content": {
                  marginLeft: "1rem",
                },
              }}
            >
              <Typography
                variant={isMobile ? "subtitle1" : "h6"}
                sx={{
                  fontSize: isMobile ? "0.9rem" : "1.25rem",
                  fontWeight: "medium",
                }}
              >
                {translate(community.name)}
              </Typography>
            </AccordionSummary>
            {/* Accordion content with station list */}
            <AccordionDetails sx={{ p: isMobile ? 1 : 2 }}>
              <Grid container spacing={isMobile ? 1 : 2}>
                {/* Map through stations in the current community */}
                {community.stations.map((station) => (
                  <Grid item xs={12} key={station.id}>
                    {/* Individual station card */}
                    <Box
                      sx={{
                        p: isMobile ? 0.5 : 1,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        backgroundColor: "background.paper",
                        borderRadius: 1,
                        // '&:hover': {
                        //   backgroundColor: 'action.hover'
                        // }
                      }}
                    >
                      {/* Station name and status section */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          flex: 1,
                          minWidth: 0, // Ensures text truncation works
                        }}
                      >
                        {/* Status icon */}
                        <Box sx={{ ml: isMobile ? 0.5 : 1 }}>
                          {getStatusIcon(station.status)}
                        </Box>
                        {/* Station name with link */}
                        <Box
                          sx={{
                            ml: isMobile ? 0.5 : 1,
                            overflow: "hidden",
                            flex: 1,
                          }}
                        >
                          <Typography
                            component={Link}
                            to={`/${createUrlFriendlyName(station.name)}`}
                            onClick={onStationSelect}
                            variant="body1"
                            sx={{
                              fontWeight: "bold",
                              fontSize: isMobile ? "0.8rem" : "0.9rem",
                              textDecoration: "none",
                              color: "inherit",
                              display: "block",
                              overflow: "hidden",
                              // textOverflow: 'ellipsis',
                              // whiteSpace: 'nowrap',
                              "&:hover": {
                                textDecoration: "underline",
                              },
                            }}
                          >
                            {translate(station.name)}
                          </Typography>
                        </Box>
                      </Box>
                      {/* Navigation arrow button */}
                      <IconButton
                        component={Link}
                        to={`/${createUrlFriendlyName(station.name)}`}
                        onClick={onStationSelect}
                        size="small"
                        sx={{
                          border: "1px solid #e0e0e0",
                          borderRadius: "50%",
                          padding: isMobile ? "2px" : "4px",
                          ml: 1,
                          flexShrink: 0,
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                      >
                        <ArrowForwardIosIcon
                          sx={{ fontSize: isMobile ? "0.7rem" : "0.8rem" }}
                        />
                      </IconButton>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        </React.Fragment>
      ))}
    </Box>
  );
}

export default AllStationsList;
